import { PRODUCT_SKUS } from './product-skus';

// Mirrors shipping_restrictions.json in shopify-checkout-apps; a country absent
// from a list cannot buy that term, and the -wk and -rf variants share caps.
// UM is omitted: checkout lists it at 3 for every sku, that table's default.

// Master switch for SRP outside the US. While this is false the storefront
// offers SRP terms to US shoppers only, whatever the lists below say.
export const INTERNATIONAL_SRP_ENABLED: boolean = false;

const DOMESTIC_COUNTRY = 'US';

export const THREE_MONTH_MAX_TWO_COUNTRIES = [
  'US', // United States
];

export const THREE_MONTH_MAX_ONE_COUNTRIES = [
  'AE', // United Arab Emirates
  'AG', // Antigua & Barbuda
  'AI', // Anguilla
  'AT', // Austria
  'AU', // Australia
  'AW', // Aruba
  'BA', // Bosnia & Herzegovina
  'BB', // Barbados
  'BD', // Bangladesh
  'BE', // Belgium
  'BG', // Bulgaria
  'BH', // Bahrain
  'BM', // Bermuda
  'BO', // Bolivia
  'BS', // Bahamas
  'BW', // Botswana
  'BZ', // Belize
  'CA', // Canada
  'CH', // Switzerland
  'CM', // Cameroon
  'CO', // Colombia
  'CR', // Costa Rica
  'CV', // Cape Verde
  'CW', // Curaçao
  'CY', // Cyprus
  'CZ', // Czechia
  'DE', // Germany
  'DK', // Denmark
  'DM', // Dominica
  'DO', // Dominican Republic
  'EC', // Ecuador
  'EE', // Estonia
  'ES', // Spain
  'ET', // Ethiopia
  'FI', // Finland
  'FR', // France
  'GA', // Gabon
  'GB', // United Kingdom
  'GG', // Guernsey
  'GH', // Ghana
  'GP', // Guadeloupe
  'GR', // Greece
  'GT', // Guatemala
  'GY', // Guyana
  'HK', // Hong Kong SAR China
  'HN', // Honduras
  'HR', // Croatia
  'HU', // Hungary
  'IE', // Ireland
  'IL', // Israel
  'IS', // Iceland
  'IT', // Italy
  'JE', // Jersey
  'KE', // Kenya
  'KH', // Cambodia
  'KN', // St. Kitts & Nevis
  'KR', // South Korea
  'KW', // Kuwait
  'KY', // Cayman Islands
  'LC', // St. Lucia
  'LI', // Liechtenstein
  'LK', // Sri Lanka
  'LT', // Lithuania
  'LU', // Luxembourg
  'LV', // Latvia
  'MA', // Morocco
  'MC', // Monaco
  'MD', // Moldova
  'MF', // St. Martin
  'MG', // Madagascar
  'MO', // Macao SAR China
  'MQ', // Martinique
  'MT', // Malta
  'MV', // Maldives
  'MW', // Malawi
  'MZ', // Mozambique
  'NA', // Namibia
  'NE', // Niger
  'NL', // Netherlands
  'NP', // Nepal
  'PA', // Panama
  'PL', // Poland
  'PT', // Portugal
  'PY', // Paraguay
  'RE', // Réunion
  'RO', // Romania
  'SE', // Sweden
  'SG', // Singapore
  'SI', // Slovenia
  'SK', // Slovakia
  'SM', // San Marino
  'SN', // Senegal
  'SV', // El Salvador
  'TC', // Turks & Caicos Islands
  'TG', // Togo
  'TH', // Thailand
  'TM', // Turkmenistan
  'TN', // Tunisia
  'TW', // Taiwan
  'TZ', // Tanzania
  'UG', // Uganda
  'UY', // Uruguay
  'VC', // St. Vincent & Grenadines
  'VN', // Vietnam
  'ZM', // Zambia
  'ZW', // Zimbabwe
];

export const SIX_MONTH_MAX_ONE_COUNTRIES = [
  'US', // United States
];

const getThreeMonthCap = (country: string): number => {
  if (THREE_MONTH_MAX_TWO_COUNTRIES.includes(country)) {
    return 2;
  }
  if (THREE_MONTH_MAX_ONE_COUNTRIES.includes(country)) {
    return 1;
  }
  return 0;
};

const getSixMonthCap = (country: string): number =>
  SIX_MONTH_MAX_ONE_COUNTRIES.includes(country) ? 1 : 0;

// AM-02, PM-02 and DM-02 3 month plans sell in the US only.
const getDomesticThreeMonthCap = (country: string): number =>
  country === DOMESTIC_COUNTRY ? 2 : 0;

const SRP_CAP_BY_SKU: Record<string, (country: string) => number> = {
  [PRODUCT_SKUS.DAILY_SYNBIOTIC_3MO]: getThreeMonthCap,
  [PRODUCT_SKUS.DAILY_SYNBIOTIC_REFILL_3MO]: getThreeMonthCap,
  [PRODUCT_SKUS.DAILY_SYNBIOTIC_6MO]: getSixMonthCap,
  [PRODUCT_SKUS.DAILY_SYNBIOTIC_REFILL_6MO]: getSixMonthCap,
  [PRODUCT_SKUS.ENERGY_FOCUS_3MO]: getDomesticThreeMonthCap,
  [PRODUCT_SKUS.SLEEP_RESTORE_3MO]: getDomesticThreeMonthCap,
  [PRODUCT_SKUS.DAILY_MULTIVITAMIN_3MO]: getDomesticThreeMonthCap,
  [PRODUCT_SKUS.ENERGY_FOCUS_REFILL_3MO]: getThreeMonthCap,
  [PRODUCT_SKUS.DAILY_MULTIVITAMIN_REFILL_3MO]: getThreeMonthCap,
  [PRODUCT_SKUS.SLEEP_RESTORE_REFILL_3MO]: getThreeMonthCap,
};

export const SRP_CAPPED_SKUS = Object.keys(SRP_CAP_BY_SKU);

// Null for skus SRP does not cap, so callers keep the plan's own maximum.
export const getCountryQuantityLimit = (
  sku: string,
  country: string,
): number | null => {
  const getCap = SRP_CAP_BY_SKU[sku];
  if (!getCap) {
    return null;
  }
  if (!INTERNATIONAL_SRP_ENABLED && country !== DOMESTIC_COUNTRY) {
    return 0;
  }
  return getCap(country);
};

// The plans API omits maxQuantity for some products.
export const DEFAULT_MAX_QUANTITY = 3;

export const isPurchasableInCountry = (
  sku: string,
  countryCode: string,
): boolean => {
  const limit = getCountryQuantityLimit(sku, countryCode);
  return limit === null || limit > 0;
};

export const getMaxQuantity = (
  sku: string,
  countryCode: string,
  planMaxQuantity?: number,
): number =>
  getCountryQuantityLimit(sku, countryCode) ??
  planMaxQuantity ??
  DEFAULT_MAX_QUANTITY;
